import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Trophy, Truck } from "lucide-react";
import { HERO } from "@/constants/testIds";

const HERO_IMG =
  "/assets/audi-r8-mot.jpeg"; // Customer Audi R8 outside OWRS Automotive Ltd unit
const SECONDARY_IMG =
  "https://customer-assets.emergentagent.com/job_tesla-performance/artifacts/6q10zvds_IMG_0707.jpeg"; // Customer Mini Clubman Cooper S with OWRS plate

export default function Hero() {
  const navigate = useNavigate();
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Audi R8 outside OWRS Automotive Ltd"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 grid-bg opacity-40" />
      </div>

      <div className="relative container-owrs pb-20 pt-24 sm:pb-28 sm:pt-32 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="hv-pulse inline-flex h-2.5 w-2.5 rounded-full bg-[var(--owrs-accent)]" />
              <span className="overline">Established 2019 · Leicester</span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[0.98] tracking-tighter">
              Performance, prestige <br />
              <span className="text-[var(--owrs-text-muted)]">&amp; a new era of</span> <br />
              <span className="text-[var(--owrs-accent)]">EV expertise.</span>
            </h1>

            <p className="mt-8 max-w-2xl text-lg sm:text-xl text-[var(--owrs-text-muted)] leading-relaxed">
              OWRS Automotive Ltd specialises in Porsche, Land Rover and performance vehicles —
              We are now bringing that same precision into the future of motoring with a
              dedicated EV and hybrid arm. Every member of our team is IMI-trained in
              high-voltage safety and repair procedures.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate("/contact")}
                data-testid={HERO.ctaPrimary}
                className="btn-primary group"
              >
                Book Your Car In
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() =>
                  document.getElementById("two-arms")?.scrollIntoView({ behavior: "smooth" })
                }
                data-testid={HERO.ctaSecondary}
                className="btn-secondary"
              >
                Explore Our Services
              </button>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl">
              <Stat
                icon={<Trophy className="h-5 w-5" />}
                label="Foundations in motorsport"
                value="Race-bred"
              />
              <Stat
                icon={<Sparkles className="h-5 w-5" />}
                label="IMI HV-trained workforce"
                value="100%"
              />
              <Stat
                icon={<Truck className="h-5 w-5" />}
                label="EV recovery partners"
                value="UK-wide"
              />
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-4 fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[var(--owrs-border-strong)]">
                <img
                  src={SECONDARY_IMG}
                  alt="Mini Clubman Cooper S in for service at OWRS Automotive"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 surface px-5 py-4 backdrop-blur-md bg-[rgba(13,31,45,0.92)]">
                <div className="overline mb-1">Recent Work</div>
                <div className="text-sm text-[var(--owrs-text)] max-w-[220px] leading-snug">
                  Mini Clubman Cooper S — service &amp; diagnostics.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="border-l border-[var(--owrs-border-strong)] pl-4">
      {icon && (
        <div className="flex items-center gap-2 text-[var(--owrs-accent)] mb-1">
          {icon}
        </div>
      )}
      <div className="font-display text-2xl sm:text-3xl font-medium uppercase">{value}</div>
      <div className="text-xs text-[var(--owrs-text-muted)] mt-1">{label}</div>
    </div>
  );
}
